import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Checkbox, useBreakpointValue } from '@chakra-ui/react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { ListReveal } from '../../../../components/list-reveal/ListReveal';
import { useIsMobile } from '../../../../hooks/use-is-mobile';
import { Accordion } from '../../../../ui-components/accordion/Accordion';
import { AccordionItem } from '../../../../ui-components/accordion/AccordionItem';
import { Button } from '../../../../ui-components/button/Button';
import { Drawer } from '../../../../ui-components/drawer/Drawer';
import { Text } from '../../../../ui-components/typography/text/Text';
import { useBopisSddFilterEnabled } from '../../hooks/use-search/useBopisSddFilterEnabled';
import { FilterTags } from '../filter-tags/FilterTags';
import { CategoryFilterSection } from './filter-sections/CategoryFilterSection';
import { ColorFilterSection } from './filter-sections/ColorFilterSection';
import { PriceFilterSection } from './filter-sections/PriceFilterSection';
import { RatingsFilterSection } from './filter-sections/RatingsFilterSection';
import { SavingsFilterSection } from './filter-sections/SavingsFilterSection';
import { SortFilterSection } from './filter-sections/SortFilterSection';
import { useIsFilterEnabled } from './filters-buttons/useIsFilterEnabled';
import type { UseSearchResult } from '../../hooks/use-search';
import type { RevealMode } from '../filter-tags/AdaptiveRevealList';

const BoldText: FC<ReactNode[]> = (chunks) =>
    chunks ? (
        <Text style="bold" as="span" color="white">
            {chunks}
        </Text>
    ) : null;

const messages = defineMessages({
    sort: {
        id: 'search.filters.sort',
        defaultMessage: 'Sort',
    },
    category: {
        id: 'search.filters.category',
        defaultMessage: 'Category',
    },
    price: {
        id: 'search.filters.price',
        defaultMessage: 'Price',
    },
    rating: {
        id: 'search.filters.rating',
        defaultMessage: 'Rating',
    },
    brand: {
        id: 'search.filters.brand',
        defaultMessage: 'Brand',
    },
    color: {
        id: 'search.filters.color',
        defaultMessage: 'Color',
    },
    material: {
        id: 'search.filters.material',
        defaultMessage: 'Material',
    },
    size: {
        id: 'search.filters.size',
        defaultMessage: 'Size',
    },
    saving: {
        id: 'search.filters.saving',
        defaultMessage: 'Discount',
    },
    title: {
        id: 'search.filters.title',
        defaultMessage: 'Filter',
    },
});

type FilterDrawerProps = {
    search: UseSearchResult;
    isOpen: boolean;
    onClose: () => void;
    expandedFilters: number[];
    onExpandedFiltersChange: (indices: number | number[]) => void;
    scrollToFilterIndex?: number | null;
};

export const FilterDrawer: FC<FilterDrawerProps> = ({
    search,
    isOpen,
    onClose,
    expandedFilters,
    onExpandedFiltersChange,
    scrollToFilterIndex = null,
}) => {
    const drawerBodyRef = useRef<HTMLDivElement>(null);
    const accordionItemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    const { filters, onChangeFilter, isFilterSelected, activeFilters, total } = search;
    const intl = useIntl();
    const isMobile = useIsMobile();
    const isFilterVisible = useIsFilterEnabled();
    const { isBopisOrSddEnabled } = useBopisSddFilterEnabled();
    const location = useLocation();

    const sizes = filters?.byName.size || [];
    const colors = filters?.byName.color || [];
    const brands = filters?.byName.brand || [];
    const materials = filters?.byName.material || [];

    const revealMode =
        useBreakpointValue<RevealMode['mode']>({
            base: 'none',
            md: 'vertical',
        }) ?? 'none';

    // Build a mapping of accordion indices to filter types
    const accordionIndexToFilterType = useMemo(() => {
        const mapping: Map<number, string> = new Map();
        let index = 0;

        if (isMobile) {
            mapping.set(index, 'sort');
            index += 1;
        }
        if (filters?.categories && filters.categories.length > 0) {
            mapping.set(index, 'categories');
            index += 1;
        }
        mapping.set(index, 'price');
        index += 1;
        if (brands.length > 0) {
            mapping.set(index, 'brand');
            index += 1;
        }
        if (materials.length > 0) {
            mapping.set(index, 'material');
            index += 1;
        }
        if (colors.length > 0) {
            mapping.set(index, 'color');
            index += 1;
        }
        if (sizes.length > 0) {
            mapping.set(index, 'size');
            index += 1;
        }
        mapping.set(index, 'rating');
        index += 1;
        if (isFilterVisible('saving')) {
            mapping.set(index, 'saving');
            index += 1;
        }

        return mapping;
    }, [
        isMobile,
        filters?.categories,
        brands.length,
        materials.length,
        colors.length,
        sizes.length,
        isFilterVisible,
    ]);

    // Check if a filter type has any active selections by examining URL params
    const hasActiveSelections = useCallback(
        (filterType: string): boolean => {
            const urlParams = new URLSearchParams(location.search);

            // 'sort' is not considered for auto-collapse
            if (filterType === 'sort') {
                return false;
            }

            // Map filter types to their URL param names
            let urlParamName = filterType;
            if (filterType === 'categories') {
                urlParamName = 'category';
            } else if (filterType === 'saving') {
                urlParamName = 'saving';
            }
            // All others match directly: brand, color, size, material, price, rating

            // Check if URL has any values for this filter type
            return urlParams.has(urlParamName) && urlParams.getAll(urlParamName).length > 0;
        },
        [location.search],
    );

    // Enhanced onChange handler that auto-collapses filters with no selections
    const handleExpandedFiltersChange = useCallback(
        (indices: number | number[]) => {
            if (Array.isArray(indices)) {
                // Filter out accordion items that have no active selections
                // but keep newly expanded items (user explicitly clicked on them)
                const newExpandedIndices = indices.filter((index) => {
                    const filterType = accordionIndexToFilterType.get(index);
                    if (!filterType) return true; // Keep if we can't determine type

                    // Always keep if it's newly being expanded (user intent)
                    if (!expandedFilters.includes(index)) {
                        return true;
                    }

                    // Keep if it has active selections
                    return hasActiveSelections(filterType);
                });

                onExpandedFiltersChange(newExpandedIndices);
            } else {
                onExpandedFiltersChange(indices);
            }
        },
        [accordionIndexToFilterType, expandedFilters, hasActiveSelections, onExpandedFiltersChange],
    );

    // Callback ref to track accordion items by their index
    const getAccordionItemRef = useCallback(
        (index: number) => (el: HTMLDivElement | null) => {
            if (el) {
                accordionItemRefs.current.set(index, el);
            } else {
                accordionItemRefs.current.delete(index);
            }
        },
        [],
    );

    // Auto-expand filters with active selections on mount/URL change
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const hasAnyActiveSelections =
            urlParams.has('brand') ||
            urlParams.has('color') ||
            urlParams.has('size') ||
            urlParams.has('material') ||
            urlParams.has('price') ||
            urlParams.has('rating') ||
            urlParams.has('category') ||
            urlParams.has('saving');

        // If there are active selections, auto-expand those filters
        if (hasAnyActiveSelections) {
            const indicesToExpand: number[] = [];

            accordionIndexToFilterType.forEach((filterType, index) => {
                if (hasActiveSelections(filterType)) {
                    indicesToExpand.push(index);
                }
            });

            if (indicesToExpand.length > 0) {
                // Merge with existing expanded filters (avoid duplicates)
                const newExpandedFilters = Array.from(
                    new Set([...expandedFilters, ...indicesToExpand]),
                );
                if (newExpandedFilters.length !== expandedFilters.length) {
                    onExpandedFiltersChange(newExpandedFilters);
                }
            }
        }
        // Only run when URL changes (not when expandedFilters changes)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Auto-collapse filters with no selections when drawer closes
    useEffect(() => {
        if (!isOpen && expandedFilters.length > 0) {
            // Filter out accordion items that have no active selections
            const filteredIndices = expandedFilters.filter((index) => {
                const filterType = accordionIndexToFilterType.get(index);
                if (!filterType) return true; // Keep if we can't determine type
                return hasActiveSelections(filterType);
            });

            if (filteredIndices.length !== expandedFilters.length) {
                onExpandedFiltersChange(filteredIndices);
            }
        }
    }, [
        isOpen,
        expandedFilters,
        accordionIndexToFilterType,
        hasActiveSelections,
        onExpandedFiltersChange,
    ]);

    // Auto-scroll to the filter when drawer opens
    useEffect(() => {
        if (
            isOpen &&
            scrollToFilterIndex !== null &&
            expandedFilters.includes(scrollToFilterIndex)
        ) {
            // Use a timeout to ensure the accordion has finished expanding
            const timer = setTimeout(() => {
                const drawerBody = drawerBodyRef.current;
                const targetElement = accordionItemRefs.current.get(scrollToFilterIndex);

                if (drawerBody && targetElement) {
                    // Calculate the position of the target element relative to the drawer body
                    const drawerBodyRect = drawerBody.getBoundingClientRect();
                    const targetRect = targetElement.getBoundingClientRect();
                    const scrollOffset = targetRect.top - drawerBodyRect.top;

                    // Scroll the drawer body to the target position
                    drawerBody.scrollTo({
                        top: drawerBody.scrollTop + scrollOffset - 20, // 20px padding from top
                        behavior: 'smooth',
                    });
                }
            }, 500); // Wait for drawer and accordion animations to complete

            return () => clearTimeout(timer);
        }
        return undefined;
    }, [isOpen, scrollToFilterIndex, expandedFilters]);

    // Track the current filter index as we render
    let currentFilterIndex = -1;
    const getNextFilterIndex = (): number => {
        currentFilterIndex += 1;
        return currentFilterIndex;
    };

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            title={intl.formatMessage(messages.title)}
            id="search-filter-drawer"
            isFullPage
            bodyRef={drawerBodyRef}
            customCallToAction={
                <section className="flex w-full flex-col gap-4">
                    {activeFilters.length > 0 ? (
                        <FilterTags search={search} mode={revealMode} maxNumRows={2} />
                    ) : null}
                    <section className="flex items-center gap-8">
                        <Button
                            id="search-filter-drawer-clear-all-filters-button"
                            variant="tertiary"
                            onClick={search.onClearAllFilters}
                            padding={0}
                        >
                            <FormattedMessage
                                id="search.filters.drawer.clear-all"
                                defaultMessage="Clear All"
                            />
                        </Button>
                        <Button
                            id="search-filter-drawer-show-results-button"
                            className="flex-1"
                            variant="primary-black"
                            textTransform="capitalize"
                            textStyle="regular"
                            onClick={() => {
                                onClose();
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                        >
                            <FormattedMessage
                                id="search.filters.show-results"
                                defaultMessage="Show Results <bold>{count}</bold>"
                                values={{
                                    count: ` (${intl.formatNumber(total)})`,
                                    bold: BoldText,
                                }}
                            />
                        </Button>
                    </section>
                </section>
            }
        >
            <Accordion allowMultiple index={expandedFilters} onChange={handleExpandedFiltersChange}>
                {isMobile && isBopisOrSddEnabled && (
                    <div ref={getAccordionItemRef(getNextFilterIndex())}>
                        <AccordionItem
                            id="search-filter-sort"
                            title={intl.formatMessage(messages.sort)}
                        >
                            <SortFilterSection search={search} />
                        </AccordionItem>
                    </div>
                )}

                {/* Categories */}
                {filters?.categories && filters.categories.length > 0 && (
                    <div ref={getAccordionItemRef(getNextFilterIndex())}>
                        <AccordionItem
                            id="search-filter-category"
                            title={intl.formatMessage(messages.category)}
                        >
                            <CategoryFilterSection search={search} />
                        </AccordionItem>
                    </div>
                )}

                {/* Price Range */}
                <div ref={getAccordionItemRef(getNextFilterIndex())}>
                    <AccordionItem
                        id="search-filter-price"
                        title={intl.formatMessage(messages.price)}
                    >
                        <PriceFilterSection search={search} />
                    </AccordionItem>
                </div>

                {/* Brands */}
                {brands.length > 0 && (
                    <div ref={getAccordionItemRef(getNextFilterIndex())}>
                        <AccordionItem
                            id="search-filter-brand"
                            title={intl.formatMessage(messages.brand)}
                        >
                            <ListReveal
                                data={brands}
                                keyExtractor={(brand) => brand.name}
                                initialNumToRender={5}
                                listContainerClassName="flex flex-col gap-4 ml-[2px]"
                                renderItem={({ item: brand }) => (
                                    <Checkbox
                                        key={brand.name}
                                        isChecked={isFilterSelected('brand', brand.name)}
                                        onChange={() => onChangeFilter('brand', brand.name)}
                                        isDisabled={brand.count === 0}
                                        variant="new-style"
                                    >
                                        {brand.name} ({brand.count})
                                    </Checkbox>
                                )}
                            />
                        </AccordionItem>
                    </div>
                )}

                {/* Materials */}
                {materials.length > 0 && (
                    <div ref={getAccordionItemRef(getNextFilterIndex())}>
                        <AccordionItem
                            id="search-filter-material"
                            title={intl.formatMessage(messages.material)}
                        >
                            <ListReveal
                                data={materials}
                                keyExtractor={(brand) => brand.name}
                                initialNumToRender={5}
                                listContainerClassName="flex flex-col gap-4 ml-[2px]"
                                renderItem={({ item: material }) => (
                                    <Checkbox
                                        key={material.name}
                                        isChecked={isFilterSelected('material', material.name)}
                                        onChange={() => onChangeFilter('material', material.name)}
                                        isDisabled={material.count === 0}
                                        variant="new-style"
                                    >
                                        {material.name} ({material.count})
                                    </Checkbox>
                                )}
                            />
                        </AccordionItem>
                    </div>
                )}

                {/* Colors */}
                {colors.length > 0 && (
                    <div ref={getAccordionItemRef(getNextFilterIndex())}>
                        <AccordionItem
                            id="search-filter-color"
                            title={intl.formatMessage(messages.color)}
                        >
                            <ColorFilterSection
                                colors={colors}
                                onFilterChange={onChangeFilter}
                                isFilterSelected={isFilterSelected}
                            />
                        </AccordionItem>
                    </div>
                )}

                {/* Sizes */}
                {sizes.length > 0 && (
                    <div ref={getAccordionItemRef(getNextFilterIndex())}>
                        <AccordionItem
                            id="search-filter-size"
                            title={intl.formatMessage(messages.size)}
                        >
                            <ListReveal
                                data={sizes}
                                keyExtractor={(size) => size.name}
                                initialNumToRender={5}
                                listContainerClassName="flex flex-col gap-4 ml-[2px]"
                                renderItem={({ item: size }) => (
                                    <Checkbox
                                        key={size.name}
                                        isChecked={isFilterSelected('size', size.name)}
                                        onChange={() => onChangeFilter('size', size.name)}
                                        isDisabled={size.count === 0}
                                        variant="new-style"
                                    >
                                        {size.name} ({size.count})
                                    </Checkbox>
                                )}
                            />
                        </AccordionItem>
                    </div>
                )}

                {/* Rating Range */}
                <div ref={getAccordionItemRef(getNextFilterIndex())}>
                    <AccordionItem
                        id="search-filter-rating"
                        title={intl.formatMessage(messages.rating)}
                    >
                        <RatingsFilterSection search={search} />
                    </AccordionItem>
                </div>

                {/* Rating Range */}
                {isFilterVisible('saving') && (
                    <div ref={getAccordionItemRef(getNextFilterIndex())}>
                        <AccordionItem
                            id="search-filter-savings"
                            title={intl.formatMessage(messages.saving)}
                        >
                            <SavingsFilterSection search={search} />
                        </AccordionItem>
                    </div>
                )}
            </Accordion>
        </Drawer>
    );
};

FilterDrawer.defaultProps = {
    scrollToFilterIndex: null,
};
